import Axios from 'axios';
import { API_ENDPOINT } from './env';
import { msalInstance } from '../../index';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { applyImpersonationHeader } from './impersonation.utils';

const API = Axios.create({
  baseURL: API_ENDPOINT,
  timeout: 120000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Navigating away from /login would loop.  Navigating away from /auth is only harmful while the MSAL popup's
// auth response (#code=... or #error=...) is still in the hash, since the parent window reads it from there.
const hasAuthResponseInHash = () => /[#&](code|error)=/.test(window.location.hash);
const isAuthFlowPath = () =>
  window.location.pathname === '/login' || (window.location.pathname === '/auth' && hasAuthResponseInHash());

// Use an interceptor to dynamically set the Authorization header on each request
API.interceptors.request.use(
  async (config) => {
    // Skip MSAL token acquisition in development environment
    if (import.meta.env.DEV) {
      return config;
    }

    applyImpersonationHeader(config.headers);

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      try {
        const response = await msalInstance.acquireTokenSilent({
          scopes: ['User.Read'],
          account: accounts[0],
        });
        config.headers.Authorization = `Bearer ${response.accessToken}`;
      } catch (error) {
        // If consent is required or token expired, redirect to login
        if (error instanceof InteractionRequiredAuthError) {
          console.warn('User interaction required');
          // Don't redirect if already on login page (causes refresh loop)
          if (!isAuthFlowPath()) {
            // Preserve redirect URL before clearing session storage
            const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
            sessionStorage.clear();
            if (redirectUrl) {
              sessionStorage.setItem('redirectAfterLogin', redirectUrl);
            }
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
        console.error('Failed to acquire token silently:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor to handle 401 unauthorized errors
API.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // If backend returns 401 Unauthorized, redirect to login
    if (error.response?.status === 401) {
      console.warn('Unauthorized');
      // Don't redirect if already on login page (causes refresh loop)
      if (!isAuthFlowPath()) {
        // Preserve redirect URL before clearing session storage
        const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
        sessionStorage.clear();
        if (redirectUrl) {
          sessionStorage.setItem('redirectAfterLogin', redirectUrl);
        }
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

export default API;
